<template>
	<Collapsible v-model:open="isOpen" class="border-b border-n-100">
		<div class="mb-16px flex items-center">
			<label
				:for="checkboxTitle"
				class="typography-s2-emp mr-8px cursor-pointer select-none whitespace-nowrap capitalize"
			>
				{{ checkboxTitle }}
			</label>

			<CollapsibleTrigger
				class="flex w-full cursor-pointer lg:order-last lg:justify-end"
			>
				<div
					:class="
						cn('bs-new-icon icon-black-chevron h-24px w-24px rotate-180', {
							'rotate-0': isOpen,
						})
					"
				></div>
			</CollapsibleTrigger>

			<Checkbox
				v-if="isParentCheckboxShown"
				class="ml-auto lg:order-first lg:ml-0 lg:mr-16px"
				v-model:checked="parentChecked"
				:id="checkboxTitle"
				:disabled="parentDisabled"
				:indeterminate="indeterminate"
				@update:checked="toggleAll"
			/>
		</div>

		<CollapsibleContent class="my-24px">
			<ul class="flex list-none flex-col gap-close-friends lg:pl-16px">
				<li
					class="flex items-center"
					v-for="(item, index) in items"
					:key="index"
				>
					<Checkbox
						class="ml-auto lg:ml-0 lg:mr-16px"
						v-model:checked="item.selected"
						:disabled="item.count === 0 && !item.selected"
						:id="item.id"
					/>
					<label
						class="flex w-full cursor-pointer items-center gap-8px peer-disabled:cursor-not-allowed max-lg:order-first"
						:for="item.id"
					>
						<div class="typography-s2-reg text-n-950">{{ item.name }}</div>
						<div class="typography-b3-reg text-n-200 lg:ml-auto">
							{{ item.count }}
						</div>
					</label>
				</li>
			</ul>
		</CollapsibleContent>
	</Collapsible>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { cn } from '@/lib/utils'
import type { CheckboxItem } from '@/lib/usePCSFilter'
import Checkbox from '@/components/ui/checkbox/Checkbox.vue'
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from '@/components/ui/collapsible'

const props = defineProps<{
	items: CheckboxItem[]
	checkboxTitle: string
	isOpenedInitially?: boolean
	isParentCheckboxShown?: boolean
}>()

const isOpen = ref(props.isOpenedInitially || false)
const parentChecked = ref(false)
const parentDisabled = computed(() =>
	props.items.every(item => item.count === 0),
)
const indeterminate = ref(false)

const toggleAll = () => {
	const newState = parentChecked.value
	props.items.forEach(item => {
		item.selected = newState && item.count > 0
	})
	indeterminate.value = false
}
const updateCheckStatus = (items: CheckboxItem[]) => {
	const total = items.filter(item => item.count > 0).length
	const checkedCount = items.filter(
		item => item.selected && item.count > 0,
	).length

	parentChecked.value = checkedCount === total && total > 0
	indeterminate.value = checkedCount > 0 && checkedCount < total
}

// Watch for changes in items prop
watch(
	() => props.items,
	newItems => {
		updateCheckStatus(newItems)
	},
	{ deep: true, immediate: true },
)
</script>
